import React, { useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { ME_QUERY } from "../../graphql/queries";
import { getAbcyaUrl, language, tokenName, userRoles, variousURLs } from "../../config";
import { handleAdScriptsLoading, loadReCaptchaScript } from "../../utilities/head";
import { gtmLogInOut } from "../../utilities/gtm";
import { useIsGameProxy, useLazyQuery } from "../../hooks";
import Main from "./main";
import { login, setShowRevalidateModal } from "../../redux/slices/loginSlice";
import { adBlockingSelector, setAdBlocking } from "../../redux/slices/userClientSlice";
import { fetchStartupData } from "../../redux/slices/dataSlice";
import { getScrollbarWidth, isCrawler, isPrerender } from "../../utilities/detection";
import { useLocation, useNavigate } from "react-router";
import { routeCodes } from "../../config/routes";
import { mpLoginSuccess, mpTimeOnPage } from "../../utilities/mixpanel";
import { visibleAdsSelector } from "../../redux/slices/adSlice";
import { getItem, removeItem, setItem } from "../../utilities/localStorage";

const tokenFromLocalStorage = getItem(tokenName);

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isGameProxy = useIsGameProxy();
  const startTime = useRef(new Date());
  const visibleAds = useSelector(visibleAdsSelector);
  const visibleAdsRef = useRef(visibleAds);
  const adBlocking = useSelector(adBlockingSelector);
  const adBlockingRef = useRef(adBlocking);
  const heightRef = useRef(document.documentElement.scrollHeight || document.body.scrollHeight);
  const scrollHeightRef = useRef(window.scrollY);

  const invalid = () => {
    // most likely token is present but not valid
    dispatch(setShowRevalidateModal());
  };

  const checkAdBlocking = () => {
    let counter = 0;
    const intervalId = setInterval(() => {
      if (counter >= 5) {
        clearInterval(intervalId);
        return;
      }
      ++counter;
      if (window.adsbygoogle) {
        if (window.adsbygoogle.push.length === 0) {
          dispatch(setAdBlocking({ adBlocking: true }));
        }
        clearInterval(intervalId);
      }
    }, 100);
    const appHead = document.getElementsByTagName("head")[0];

    variousURLs.adDetect.forEach((url) => {
      const googleAdScript = document.createElement("script");

      googleAdScript.onerror = () => {
        appHead.removeChild(googleAdScript);
        dispatch(setAdBlocking({ adBlocking: true }));
      };

      googleAdScript.onload = () => {
        appHead.removeChild(googleAdScript);
      };

      googleAdScript.type = "text/javascript";
      googleAdScript.src = url;
      googleAdScript.defer = true;
      appHead.appendChild(googleAdScript);
    });
  };

  const onCompleted = (data) => {
    if (data && data.me) {
      const {
        subscription,
        games,
        hiddenCategories,
        email,
        id,
        userType,
        logins,
        createdAt,
        token: tokenFromQuery,
      } = data.me;

      const tokenRole = jwtDecode(tokenFromLocalStorage).role;

      if (
        subscription.planGroup !== tokenRole &&
        !([userRoles.classroom, userRoles.school].includes(subscription.planGroup) && tokenRole === userRoles.student)
      ) {
        // If subscription role comes back different than the token role
        // and the token role ISN'T a student with a still valid school/classroom subscription role
        // log them out and make them log back in as their role has changed and need an updated token
        // ie was premium subscriber and has canceled/lapsed to a public/free user
        removeItem(tokenName);
        invalid();
      } else {
        dispatch(
          login({
            role: tokenRole,
            planId: subscription.planId,
            billingPlatform: subscription.billingPlatform,
            games,
            hiddenCategories,
            email,
            id,
          }),
        );
        setItem("planId", subscription.planId);
        setItem("billingPlatform", subscription.billingPlatform);
        setItem("games", JSON.stringify(games));
        setItem("hiddenCategories", JSON.stringify(hiddenCategories));
        setItem(tokenName, tokenFromQuery);

        gtmLogInOut({ role: tokenRole, billingPlatform: subscription.billingPlatform });

        // If first time login, "isLoginBeforeByForm" does not exist in local storage. Form could be the login modal or login form on the free signup page.
        // If not first time login, "isLoginBeforeByForm" exists and set to any value (here we set it to "1")
        const isLoginBeforeByForm = getItem("isLoginBeforeByForm");
        if (isLoginBeforeByForm !== null) {
          mpLoginSuccess(tokenRole, logins, false, userType, subscription.planGroup, id, createdAt, location.pathname);
        } else {
          mpLoginSuccess(tokenRole, logins, true, userType, subscription.planGroup, id, createdAt, location.pathname);
          setItem("isLoginBeforeByForm", "1");
        }
      }
    } else {
      invalid();
    }
  };

  const { executeQuery: meQuery } = useLazyQuery(ME_QUERY, onCompleted);

  const timeOnPageHandler = (pathname) => {
    const endTime = new Date();
    const elapsed = Math.floor((endTime - startTime.current) / 1000);
    const scrollPercent = Math.floor(
      (scrollHeightRef.current / (heightRef.current - document.documentElement.clientHeight)) * 100,
    );
    const numAdsShown = Object.values(visibleAdsRef.current).reduce((acc, ad) => {
      let count = acc;
      count += ad;
      return count;
    }, 0);
    mpTimeOnPage(
      elapsed,
      scrollPercent,
      numAdsShown,
      adBlockingRef.current,
      Object.keys(visibleAdsRef.current).length,
      pathname,
    );
  };

  const scrollHandler = () => {
    scrollHeightRef.current = window.scrollY;
  };

  useEffect(() => {
    visibleAdsRef.current = visibleAds;
  }, [visibleAds]);

  useEffect(() => {
    adBlockingRef.current = adBlocking;
  }, [adBlocking]);

  useEffect(() => {
    startTime.current = new Date();
    scrollHeightRef.current = window.scrollY;
    return () => {
      timeOnPageHandler(location.pathname + location.search);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (document?.documentElement?.scrollHeight || document?.body?.scrollHeight) {
      heightRef.current = document?.documentElement?.scrollHeight || document?.body?.scrollHeight;
    }
  }, [document.documentElement.scrollHeight, document.body.scrollHeight]);

  useEffect(() => {
    // did mount
    const currentHostname = new URL(getAbcyaUrl()).hostname;
    const referrer = document.referrer ? new URL(document.referrer).hostname : null;

    const isHomePath = location.pathname === routeCodes.HOME;
    const notHomeRefer = referrer !== currentHostname;

    if (!isGameProxy && !isCrawler && !isPrerender) {
      // look for language preference token
      const langPref = getItem(language.preferenceToken);

      if (location.hash && location.hash === language.resetHash) {
        if (langPref) {
          // if existing language preference, remove it
          removeItem(language.preferenceToken);
        }
        // replace history location with url minus hash
        navigate(location.pathname, { replace: true });
      } else {
        // if home page redirect user to prefered localized site
        if (langPref && isHomePath && notHomeRefer) {
          window.location.replace(getAbcyaUrl(langPref));
        }
      }
    }

    document.documentElement.style.setProperty("--scrollbar-width", `${getScrollbarWidth()}px`);

    dispatch(fetchStartupData());
    if (tokenFromLocalStorage) {
      meQuery();
    }
    if (!isGameProxy) {
      loadReCaptchaScript();
      checkAdBlocking();
    }

    window.addEventListener("beforeunload", () => timeOnPageHandler(window.location.pathname + window.location.search));
    window.addEventListener("scroll", scrollHandler);
  }, []);

  useEffect(() => {
    // did update
    if (!isGameProxy) {
      handleAdScriptsLoading();
    }
  });

  // Prevent manual game iframe injection
  useEffect(() => {
    if (!isGameProxy) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            const checkAndHandleIframe = (iframe) => {
              if (iframe.src && iframe.src.includes("/games/")) {
                const isReactInjected = Object.keys(iframe).some((key) => key.startsWith("__react"));
                if (!isReactInjected) {
                  iframe.remove();
                }
              }
            };

            // Check if injected directly
            if (node.tagName === "IFRAME") {
              checkAndHandleIframe(node);
            }

            // Check if injected inside a wrapper
            if (node.querySelectorAll) {
              node.querySelectorAll("iframe").forEach((iframe) => {
                checkAndHandleIframe(iframe);
              });
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => observer.disconnect();
    }
  }, []);

  return <Main />;
}
